/**
 * @file THE bake. Turns merged, non-indexed, albedo-tinted geometry into the two
 * Float32 vertex-colour channels the whole renderer is built on:
 * `color` = day, `color1` = night, blended at runtime by `uNight`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CONTRACT (agreed with T3 / `build/mergeGeo.js`)
 * ─────────────────────────────────────────────────────────────────────────────
 * IN : `position` (world space, no matrix), `normal` (identical per triangle),
 *      `color` = LINEAR ALBEDO (tint x per-face jitter).
 * OUT: `color` OVERWRITTEN with the lit day channel, `color1` ADDED with night.
 *      Both Float32 x3 and deliberately unclamped — values above 1 are what feed
 *      the bloom threshold.
 *
 * Run the bake on PRE-CHUNK, per-material geometry: `world/Chunker.js` copies
 * `color`/`color1` verbatim, so chunking afterwards is free and lossless.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT IS FAST ENOUGH TO DO ON THE CPU AT ALL
 * ─────────────────────────────────────────────────────────────────────────────
 * 1. DEDUP. Merged geometry repeats every vertex three times per triangle and
 *    every corner once per adjacent face. Keyed by `quantize(pos, 0.02)` plus
 *    the DOMINANT NORMAL AXIS, the lighting solve collapses 3-5x. The cache
 *    stores the LIGHT (the bracketed term), never the final colour — albedo
 *    still varies per face from the tint jitter, so it is applied afterwards.
 * 2. The rays are short. AO is capped at `ao.radius`, sun rays at
 *    `cfg.sunMaxDist`, and `OccluderSet`'s DDA drops out the moment the ray
 *    climbs over the tallest thing in the level.
 * 3. Nothing is allocated inside the loop. Not one object, not one array.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NO three.js IMPORT — ON PURPOSE
 * ─────────────────────────────────────────────────────────────────────────────
 * `bake.worker.js` imports this module. Pulling `three/webgpu` into a worker
 * bundle costs ~1 MB for one class. {@link applyBake} therefore builds its
 * attributes from `positionAttribute.constructor`, which IS `THREE.BufferAttribute`
 * for anything `GeoBuilder.merge()` / `mergeNonIndexed()` produced.
 *
 * @module engine/bake/bakeVertexLight
 */

import { DAY_DEFAULTS, mergeConfig } from './BakeConfig.js';
import { onb } from './Occluders.js';

/** @typedef {import('../types.js').BakeConfig} BakeConfig */
/** @typedef {import('../types.js').BakeResult} BakeResult */
/** @typedef {import('./Occluders.js').OccluderSet} OccluderSet */
/** @typedef {import('./Emitters.js').EmitterSet} EmitterSet */

const TAU = Math.PI * 2;
/** Ray origins are pushed this far along the normal (see Occluders.js).
 *  Exported for `sunKeys.js`, which must push by the same amount or a key's
 *  shadow terminator lands a few centimetres off this bake's. */
export const ORIGIN_PUSH = 0.05;

/** Salts the sun cone's per-vertex jitter. Shared with `sunKeys.js` so a key
 *  baked at `cfg.sunDir` reproduces {@link solveVertexLight}'s `sunVis`. */
export const SUN_JITTER_SALT = 0x5bf03635;

/** The interior skylight direction — see `solveVertexLight`'s indoor branch. */
const UP_DIR = Object.freeze({ x: 0, y: 1, z: 0 });

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/**
 * @param {number} i
 * @returns {number} van der Corput base 2, in [0,1)
 */
function radicalInverse2(i) {
  let bits = i >>> 0;
  bits = ((bits << 16) | (bits >>> 16)) >>> 0;
  bits = (((bits & 0x55555555) << 1) | ((bits & 0xaaaaaaaa) >>> 1)) >>> 0;
  bits = (((bits & 0x33333333) << 2) | ((bits & 0xcccccccc) >>> 2)) >>> 0;
  bits = (((bits & 0x0f0f0f0f) << 4) | ((bits & 0xf0f0f0f0) >>> 4)) >>> 0;
  bits = (((bits & 0x00ff00ff) << 8) | ((bits & 0xff00ff00) >>> 8)) >>> 0;
  return bits / 4294967296;
}

/**
 * Integer hash of the QUANTISED position + normal axis. Must depend only on the
 * cache key, or two vertices sharing a cache slot would disagree about their ray
 * pattern and the bake would stop being deterministic.
 *
 * ⚠ EXPORTED FOR `sunKeys.js` ONLY, and it must stay byte-identical there: a key
 * baked at the level's `sunDir` has to reproduce this bake's `sunVis` exactly,
 * and the sun cone's jitter is seeded from this hash. A second, drifted copy of
 * this function would show up as the noon key disagreeing with the noon bake.
 *
 * @param {number} x @param {number} y @param {number} z @param {number} a
 * @returns {number} uint32
 */
export function hash4i(x, y, z, a) {
  let h = Math.imul(x | 0, 0x27d4eb2d) ^ Math.imul(y | 0, 0x165667b1) ^ Math.imul(z | 0, 0x9e3779b1) ^ Math.imul(a | 0, 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39);
  return (h ^ (h >>> 15)) >>> 0;
}

/**
 * Cheap deterministic value noise in [0,1) from a lattice hash — used only for
 * the optional albedo grading, where a real noise object would mean importing
 * `math/noise.js` into the worker for one call.
 * @param {number} x @param {number} y @param {number} z
 * @returns {number}
 */
function valueNoise3(x, y, z) {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const zi = Math.floor(z);
  const fx = x - xi;
  const fy = y - yi;
  const fz = z - zi;
  const sx = fx * fx * (3 - 2 * fx);
  const sy = fy * fy * (3 - 2 * fy);
  const sz = fz * fz * (3 - 2 * fz);
  let acc = 0;
  for (let k = 0; k < 8; k++) {
    const cx = k & 1;
    const cy = (k >> 1) & 1;
    const cz = (k >> 2) & 1;
    const w = (cx ? sx : 1 - sx) * (cy ? sy : 1 - sy) * (cz ? sz : 1 - sz);
    acc += w * (hash4i(xi + cx, yi + cy, zi + cz, 7) / 4294967296);
  }
  return acc;
}

/**
 * mulberry32, inlined so the worker does not need `math/rng.js`.
 * Exported alongside {@link hash4i} and for the same reason.
 * @param {number} seed
 * @returns {() => number}
 */
export function rngFrom(seed) {
  let s = seed >>> 0;
  return function rng() {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Dedup key — SHARED WITH sunKeys.js
// ─────────────────────────────────────────────────────────────────────────────
// Both bakes must collapse vertices onto the SAME slots, or a key's shadow and
// this bake's ambient would be sampled at different points on the same wall.
// Factored into two tiny pure functions rather than copy-pasted: a drifted copy
// is invisible until it is a lighting seam. Neither allocates, so the "nothing
// is allocated inside the loop" rule in the header still holds.

/**
 * Dominant SIGNED normal axis, 0..5. Merged geometry has byte-identical normals
 * across a triangle, so this collapses coplanar corners safely.
 * @param {number} nx @param {number} ny @param {number} nz
 * @returns {number} 0..5
 */
export function normalAxis(nx, ny, nz) {
  const ax = nx < 0 ? -nx : nx;
  const ay = ny < 0 ? -ny : ny;
  const az = nz < 0 ? -nz : nz;
  return ax >= ay && ax >= az ? (nx < 0 ? 1 : 0) : ay >= az ? (ny < 0 ? 3 : 2) : nz < 0 ? 5 : 4;
}

/** 2^18 — quantised X/Z slots. See {@link slotKey}. */
const SLOT_SPAN_XZ = 262144;
/** 2^17 — half of {@link SLOT_SPAN_XZ}; +-2621 m at `dedupQuant` 0.02. */
const SLOT_HALF_XZ = 131072;
/** 2^14 — quantised Y slots. */
const SLOT_SPAN_Y = 16384;
/** 2^13 — half of {@link SLOT_SPAN_Y}; +-163 m at `dedupQuant` 0.02. */
const SLOT_HALF_Y = 8192;

/**
 * Packed dedup key, or -1 when the vertex is out of the packable range.
 *
 * Packed into a double's exact-integer range (2^53), and the split is NOT
 * uniform — it is 18 bits of X, 14 of Y, 18 of Z and 3 of axis, which sums to
 * exactly 53. A vertex outside the range BYPASSES the cache rather than
 * aliasing onto some unrelated vertex's lighting: it is still solved, just
 * individually, so the answer is right and only the cost is wrong.
 *
 * ⚠ THE SPLIT IS DELIBERATELY LOPSIDED, and reverting it to a tidy 16/16/16
 * would silently cost 3-5x on a big world. A world is WIDE and THIN: at the
 * 0.02 m default quantisation 18 bits buys +-2621 m of X and Z, while 14 bits
 * is still +-163 m of Y — and Y only has to cover the shop interiors at -100
 * and the tallest tower at ~+60. The old 16/16/16 gave +-655 m in every axis,
 * which wasted more than half its Y range on empty sky and ran out of X and Z
 * the moment `cfg.world` went past ~1300 m. There was no error when it did:
 * the outer ring of the map just quietly stopped deduping, in the main bake
 * AND in every one of the sun keys.
 *
 * ⚠ `sunKeys.js` IMPORTS this function rather than re-implementing it, so both
 * paths change together. `tests/sunkeys.test.mjs` asserts they agree bit for
 * bit; `tests/bake.test.mjs` pins the packing itself.
 *
 * @param {number} px @param {number} py @param {number} pz
 * @param {number} axis From {@link normalAxis}.
 * @param {number} invQ `1 / dedupQuant`.
 * @returns {number} Key, or -1 for "do not cache this one".
 */
export function slotKey(px, py, pz, axis, invQ) {
  const qx = Math.round(px * invQ);
  const qy = Math.round(py * invQ);
  const qz = Math.round(pz * invQ);
  if (qx < -SLOT_HALF_XZ || qx >= SLOT_HALF_XZ) return -1;
  if (qy < -SLOT_HALF_Y || qy >= SLOT_HALF_Y) return -1;
  if (qz < -SLOT_HALF_XZ || qz >= SLOT_HALF_XZ) return -1;
  // (((x * 2^14) + y) * 2^18 + z) * 8 + axis. The largest value this can
  // produce is exactly 2^53 - 1, the last integer a double represents exactly.
  return (((qx + SLOT_HALF_XZ) * SLOT_SPAN_Y + (qy + SLOT_HALF_Y)) * SLOT_SPAN_XZ + (qz + SLOT_HALF_XZ)) * 8 + axis;
}

/**
 * The half-extents {@link slotKey} can cache, in METRES, at a given quantum.
 * Exported so a level generator can assert its own world fits before it pays
 * for a bake that silently costs 3-5x more than it should.
 *
 * @param {number} [dedupQuant=0.02]
 * @returns {{xz: number, y: number}} Half-extents in metres.
 */
export function slotKeyRange(dedupQuant = 0.02) {
  return { xz: SLOT_HALF_XZ * dedupQuant, y: SLOT_HALF_Y * dedupQuant };
}

/**
 * The per-vertex ray-pattern seed. Taken on a FIXED 2 cm lattice, never on
 * `dedupQuant`: turning dedup off must change the COST of the bake, not its
 * answer. (It also means two coincident vertices from different generators
 * shoot the same rays and cannot disagree by a ray's worth of AO.)
 * @param {number} px @param {number} py @param {number} pz @param {number} axis
 * @returns {number} uint32
 */
export function vertexHash(px, py, pz, axis) {
  return hash4i(Math.round(px * 50), Math.round(py * 50), Math.round(pz * 50), axis);
}

// ─────────────────────────────────────────────────────────────────────────────
// Ambient occlusion
// ─────────────────────────────────────────────────────────────────────────────

const _ob = new Float64Array(6);

/**
 * Cosine-weighted hemisphere AO. `rays` Hammersley directions around `n`,
 * rotated by `hash(pos)` so neighbouring vertices never share a pattern (an
 * unrotated set makes AO banding you can see across a wall). Each hit is
 * weighted `1 - t/radius`, so a wall 5 cm away occludes and one 4.9 m away
 * barely registers.
 *
 * @param {OccluderSet|null} occ
 * @param {number} px @param {number} py @param {number} pz Surface point (NOT
 *   pushed — this function applies the `+0.05*n` push itself).
 * @param {number} nx @param {number} ny @param {number} nz Unit normal.
 * @param {any} cfg Merged config (see {@link mergeConfig}).
 * @param {number} [hash=0] Rotation seed; pass `hash4i(...)` of the cache key.
 * @returns {number} `ao.floor` .. 1
 */
export function sampleAO(occ, px, py, pz, nx, ny, nz, cfg, hash = 0) {
  const ao = cfg.ao ?? DAY_DEFAULTS.ao;
  if (!occ || occ.count === 0) return 1;
  const rays = ao.rays;
  const R = ao.radius;
  if (!(R > 0) || rays <= 0) return 1;

  onb(nx, ny, nz, _ob);
  const tx = _ob[0];
  const ty = _ob[1];
  const tz = _ob[2];
  const bx = _ob[3];
  const by = _ob[4];
  const bz = _ob[5];

  const ox = px + nx * ORIGIN_PUSH;
  const oy = py + ny * ORIGIN_PUSH;
  const oz = pz + nz * ORIGIN_PUSH;
  const rot = (hash >>> 0) / 4294967296;

  let sum = 0;
  for (let i = 0; i < rays; i++) {
    const u = (i + 0.5) / rays;
    let v = radicalInverse2(i) + rot;
    v -= Math.floor(v);
    const sinT = Math.sqrt(u); // cosine-weighted: sin = sqrt(u), cos = sqrt(1-u)
    const cosT = Math.sqrt(1 - u);
    const phi = TAU * v;
    const a = Math.cos(phi) * sinT;
    const b = Math.sin(phi) * sinT;
    const dx = tx * a + bx * b + nx * cosT;
    const dy = ty * a + by * b + ny * cosT;
    const dz = tz * a + bz * b + nz * cosT;
    const t = occ.rayT(ox, oy, oz, dx, dy, dz, R);
    if (t >= 0) sum += 1 - t / R;
  }
  const v = 1 - ao.strength * (sum / rays);
  return v < ao.floor ? ao.floor : v > 1 ? 1 : v;
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-vertex solve
// ─────────────────────────────────────────────────────────────────────────────

const _emit = new Float32Array(3);

/**
 * Solves the day + night LIGHT (no albedo) at one point. Writes 6 floats into
 * `out`: `[dayR,dayG,dayB, nightR,nightG,nightB]`.
 *
 * ⚠ If `out` is 9 long it ALSO writes the day channel's direction-free part —
 * `[ambR,ambG,ambB]` = hemi + bounce + floor, i.e. everything except the sun.
 * That is what `sunKeys.js` needs to reconstruct the day channel with a moving
 * sun: `day === ambient + sunColor * sunIntensity * max(dot(n,L),0) * sunVis`,
 * which `tests/sunkeys.test.mjs` asserts as an exact identity. Callers passing
 * a 6-long `out` are unaffected and pay nothing.
 *
 * @param {number} px @param {number} py @param {number} pz
 * @param {number} nx @param {number} ny @param {number} nz
 * @param {OccluderSet|null} occ
 * @param {EmitterSet|null} emitters
 * @param {any} cfg Merged config.
 * @param {number} hash Deterministic per-vertex seed.
 * @param {Float32Array|number[]} out Length >= 6.
 * @returns {number} The AO value (the caller wants it for the probe grid).
 */
export function solveVertexLight(px, py, pz, nx, ny, nz, occ, emitters, cfg, hash, out) {
  const ao = sampleAO(occ, px, py, pz, nx, ny, nz, cfg, hash);

  // Below `interiorBelowY` the world is INTERIOR (the city's shop rooms at
  // y = -100): there is no visible sky or sun disc down there, so the sun
  // becomes a straight-up SKYLIGHT — floors and table tops lit, walls on
  // ambient, and no lateral wall-shadow whatever the hour. `splitDay` folds
  // the whole term into the frozen `ambient` output (see the split loop), so
  // an interior is exempt from the moving sun BY CONSTRUCTION rather than by
  // a runtime gate. Default -Infinity: no geometry qualifies, nothing changes.
  const indoor = py < cfg.interiorBelowY;
  const L = indoor ? UP_DIR : cfg.sunDir;
  const ndl = nx * L.x + ny * L.y + nz * L.z;
  let sunVis = 0;
  if (ndl > 0) {
    if (!occ || occ.count === 0) sunVis = 1;
    else {
      sunVis = occ.sunVis(
        px + nx * ORIGIN_PUSH,
        py + ny * ORIGIN_PUSH,
        pz + nz * ORIGIN_PUSH,
        L,
        {
          rays: cfg.sunRays,
          cone: (cfg.sunConeDeg * Math.PI) / 180,
          maxT: cfg.sunMaxDist,
          rng: rngFrom(hash ^ SUN_JITTER_SALT),
        },
      );
    }
  }

  const M = cfg.moonDir;
  const mdl = nx * M.x + ny * M.y + nz * M.z;
  let moonVis = 0;
  if (mdl > 0) {
    if (!occ || occ.count === 0 || cfg.moonRays <= 0) moonVis = 1;
    else {
      moonVis = occ.sunVis(
        px + nx * ORIGIN_PUSH,
        py + ny * ORIGIN_PUSH,
        pz + nz * ORIGIN_PUSH,
        M,
        {
          rays: cfg.moonRays,
          cone: (cfg.sunConeDeg * Math.PI) / 180,
          maxT: cfg.sunMaxDist,
          rng: rngFrom(hash ^ 0x1b873593),
        },
      );
    }
  }

  if (emitters && emitters.count > 0) emitters.gather(px, py, pz, nx, ny, nz, _emit);
  else {
    _emit[0] = 0;
    _emit[1] = 0;
    _emit[2] = 0;
  }

  // Sky/ground hemisphere by normal tilt — the cheap wrap light that keeps
  // upward faces cool and downward faces earthy.
  const hemiT = (ny + 1) * 0.5;
  const sunI = cfg.sunIntensity * (ndl > 0 ? ndl : 0) * sunVis;
  const moonI = cfg.moonIntensity * (mdl > 0 ? mdl : 0) * moonVis * cfg.moonVisScale;
  const hemiI = cfg.hemiIntensity * ao;
  const bounceI = cfg.bounceIntensity * ao;
  const nHemiI = cfg.nightHemiIntensity * ao;
  const emitI = cfg.emitterIntensity * ao;

  const split = out.length >= 9;
  for (let c = 0; c < 3; c++) {
    const hemi = cfg.hemiGround[c] + (cfg.hemiSky[c] - cfg.hemiGround[c]) * hemiT;
    const ambient = hemi * hemiI + cfg.bounceColor[c] * bounceI + cfg.ambientFloor;
    out[c] = cfg.sunColor[c] * sunI + ambient;
    out[3 + c] = cfg.moonColor[c] * moonI + cfg.nightHemi[c] * nHemiI + _emit[c] * emitI + cfg.nightFloor;
    // Indoor: the skylight is FROZEN — it rides the direction-free channel so
    // the room's whole day light survives `sunTint` being zeroed (bakeArrays).
    if (split) out[6 + c] = indoor ? out[c] : ambient;
  }
  return ao;
}

// ─────────────────────────────────────────────────────────────────────────────
// Array-level bake (this is what the worker runs)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The bake proper, on raw typed arrays.
 *
 * @param {Float32Array|ArrayLike<number>} position World-space, non-indexed.
 * @param {Float32Array|ArrayLike<number>} normal Per-face (3 identical per tri).
 * @param {Float32Array|ArrayLike<number>|null} albedo Linear RGB per vertex, or
 *   `null` for white.
 * @param {OccluderSet|null} occ
 * @param {EmitterSet|null} emitters
 * @param {Partial<BakeConfig>|null} [config]
 * @param {((done: number, total: number) => void)|null} [onTick] Called every
 *   8192 vertices; the worker turns it into `postMessage({progress})`.
 * @returns {{color: Float32Array, color1: Float32Array, ms: number, verts: number, unique: number}}
 */
export function bakeArrays(position, normal, albedo, occ, emitters, config = null, onTick = null) {
  const t0 = now();
  const cfg = config && config.__merged ? config : mergeConfig(DAY_DEFAULTS, config);
  const n = (position?.length ?? 0) / 3;
  const color = new Float32Array(n * 3);
  const color1 = new Float32Array(n * 3);
  if (!n) return { color, color1, ambient: null, sunTint: null, ms: 0, verts: 0, unique: 0 };

  if (occ && typeof occ.build === 'function' && !occ._packed) occ.build({ cell: occ.cell ?? 4 });
  if (emitters && typeof emitters.build === 'function' && !emitters._packed) emitters.build({ cell: emitters.cell ?? 8 });

  const q = cfg.dedupQuant > 0 ? cfg.dedupQuant : 0;
  const invQ = q > 0 ? 1 / q : 0;
  /** @type {Map<number, number>|null} */
  const cache = q > 0 ? new Map() : null;

  // `splitDay` adds the direction-free half of the day channel, for levels whose
  // sun moves (see sunKeys.js). It costs a wider store and two more output
  // arrays, so it is OPT-IN — a gallery or a dev scene pays nothing.
  const split = !!cfg.splitDay;
  const stride = split ? 9 : 6;
  const ambient = split ? new Float32Array(n * 3) : null;
  const sunTint = split ? new Float32Array(n * 3) : null;

  // One slot per unique vertex. Worst case = no dedup at all.
  const store = new Float32Array(n * stride);
  const light = new Float32Array(stride);
  let unique = 0;

  const grade = cfg.gradeAmount > 0;
  const gs = cfg.gradeScale;

  for (let i = 0; i < n; i++) {
    const p = i * 3;
    const px = position[p];
    const py = position[p + 1];
    const pz = position[p + 2];
    const nx = normal ? normal[p] : 0;
    const ny = normal ? normal[p + 1] : 1;
    const nz = normal ? normal[p + 2] : 0;

    const axis = normalAxis(nx, ny, nz);
    const hash = vertexHash(px, py, pz, axis);

    let slot;
    if (cache) {
      const key = slotKey(px, py, pz, axis, invQ);
      if (key >= 0) {
        slot = cache.get(key);
        if (slot === undefined) {
          solveVertexLight(px, py, pz, nx, ny, nz, occ, emitters, cfg, hash, light);
          slot = unique++;
          store.set(light, slot * stride);
          cache.set(key, slot);
        }
      }
    }
    if (slot === undefined) {
      solveVertexLight(px, py, pz, nx, ny, nz, occ, emitters, cfg, hash, light);
      slot = unique++;
      store.set(light, slot * stride);
    }

    const s = slot * stride;
    let ar = albedo ? albedo[p] : 1;
    let ag = albedo ? albedo[p + 1] : 1;
    let ab = albedo ? albedo[p + 2] : 1;

    if (grade) {
      // Weathering: drag the albedo toward the grade colour where the noise is
      // high. Position-driven, so it crosses face boundaries seamlessly.
      const g = valueNoise3(px * gs, py * gs, pz * gs);
      const k = cfg.gradeAmount * g;
      ar += (cfg.gradeColor[0] - ar) * k;
      ag += (cfg.gradeColor[1] - ag) * k;
      ab += (cfg.gradeColor[2] - ab) * k;
    }

    color[p] = ar * store[s];
    color[p + 1] = ag * store[s + 1];
    color[p + 2] = ab * store[s + 2];
    color1[p] = ar * store[s + 3];
    color1[p + 1] = ag * store[s + 4];
    color1[p + 2] = ab * store[s + 5];

    if (split) {
      // The graded albedo is applied to both halves exactly as it is to `color`,
      // so `ambient + sunTint * ndl * sunVis` reconstructs `color` identically —
      // including the weathering, which would otherwise only be in one of them.
      //
      // INTERIOR verts (below `cfg.interiorBelowY`) zero the tint instead: their
      // `ambient` already carries the whole frozen day (skylight included — see
      // solveVertexLight), so the live sun term must contribute NOTHING or the
      // room would be lit twice and swing with the hour.
      ambient[p] = ar * store[s + 6];
      ambient[p + 1] = ag * store[s + 7];
      ambient[p + 2] = ab * store[s + 8];
      const indoor = py < cfg.interiorBelowY;
      sunTint[p] = indoor ? 0 : ar * cfg.sunColor[0] * cfg.sunIntensity;
      sunTint[p + 1] = indoor ? 0 : ag * cfg.sunColor[1] * cfg.sunIntensity;
      sunTint[p + 2] = indoor ? 0 : ab * cfg.sunColor[2] * cfg.sunIntensity;
    }

    if (onTick && (i & 8191) === 0) onTick(i, n);
  }

  return { color, color1, ambient, sunTint, ms: now() - t0, verts: n, unique };
}

/**
 * Bakes ONE geometry on the calling thread. Use it for props, creatures and
 * anything under ~30k vertices; go through {@link bakeAsync} for a level.
 *
 * Does NOT modify the geometry — pass the result to {@link applyBake}.
 *
 * @param {any} geo A `THREE.BufferGeometry` (duck-typed: `getAttribute`).
 * @param {OccluderSet|null} occ
 * @param {EmitterSet|null} emitters
 * @param {Partial<BakeConfig>|null} [cfg]
 * @returns {{color: Float32Array, color1: Float32Array, ms: number, verts: number, unique: number}}
 */
export function bakeSync(geo, occ, emitters, cfg = null) {
  const pos = geo?.getAttribute?.('position');
  if (!pos) throw new Error('bakeSync: geometry has no `position` attribute');
  if (geo.index) throw new Error('bakeSync: geometry must be non-indexed (see mergeGeo.assertNonIndexed)');
  const nor = geo.getAttribute('normal');
  const col = geo.getAttribute('color');
  return bakeArrays(pos.array, nor ? nor.array : null, col ? col.array : null, occ, emitters, cfg);
}

/**
 * Attaches a bake result to a geometry: `color` is OVERWRITTEN (albedo is gone
 * after this) and `color1` is ADDED.
 *
 * Asserts both attributes land and match the vertex count — `render/nodes.js`'s
 * `vertexColor(1)` silently falls back to white when `color1` is missing, which
 * looks like "the night channel is broken" three layers away from the cause.
 *
 * @param {any} geo
 * @param {{color: Float32Array, color1: Float32Array}} res
 * @returns {any} `geo`
 */
export function applyBake(geo, res) {
  const pos = geo?.getAttribute?.('position');
  if (!pos) throw new Error('applyBake: geometry has no `position` attribute');
  if (pos.isInterleavedBufferAttribute) {
    throw new Error('applyBake: interleaved attributes are not supported (merged geometry never is)');
  }
  const need = pos.count * 3;
  if (!res || !(res.color instanceof Float32Array) || !(res.color1 instanceof Float32Array)) {
    throw new Error('applyBake: result must carry Float32Array `color` and `color1`');
  }
  if (res.color.length !== need || res.color1.length !== need) {
    throw new Error(
      `applyBake: length mismatch — position ${pos.count} verts needs ${need} floats, ` +
        `got color ${res.color.length} / color1 ${res.color1.length}`,
    );
  }

  // See the module header: this is how we avoid importing three into the worker
  // bundle. For anything GeoBuilder/mergeGeo produced, Ctor === BufferAttribute.
  const Ctor = /** @type {any} */ (pos.constructor);
  geo.setAttribute('color', new Ctor(res.color, 3));
  geo.setAttribute('color1', new Ctor(res.color1, 3));

  const a = geo.getAttribute('color');
  const b = geo.getAttribute('color1');
  if (!a || !b || a.count !== pos.count || b.count !== pos.count) {
    throw new Error('applyBake: failed to attach both colour channels');
  }
  return geo;
}

/**
 * The KEYED variant of {@link applyBake}: attaches the split day channel so the
 * sun can move.
 *
 * `color` becomes the direction-free AMBIENT half rather than the full day
 * channel, and `color2` carries the sun tint. That is a real change of meaning
 * for `color` — a material reading it WITHOUT `{sun: true}` will render this
 * geometry with no sunlight at all, which looks like permanent heavy overcast.
 * The two always move together.
 *
 * @param {any} geo
 * @param {{color1: Float32Array, ambient: Float32Array|null, sunTint: Float32Array|null}} res
 *   A `bakeArrays` result baked with `splitDay: true`.
 * @returns {any} `geo`
 */
export function applySplitBake(geo, res) {
  const pos = geo?.getAttribute?.('position');
  if (!pos) throw new Error('applySplitBake: geometry has no `position` attribute');
  const need = pos.count * 3;
  if (!res?.ambient || !res?.sunTint) {
    throw new Error('applySplitBake: result was not baked with `splitDay: true`');
  }
  if (res.ambient.length !== need || res.sunTint.length !== need || res.color1.length !== need) {
    throw new Error(
      `applySplitBake: length mismatch — ${pos.count} verts needs ${need} floats, got ` +
        `ambient ${res.ambient.length} / sunTint ${res.sunTint.length} / color1 ${res.color1.length}`,
    );
  }

  const Ctor = /** @type {any} */ (pos.constructor);
  geo.setAttribute('color', new Ctor(res.ambient, 3));
  geo.setAttribute('color1', new Ctor(res.color1, 3));
  geo.setAttribute('color2', new Ctor(res.sunTint, 3));
  return geo;
}

// ─────────────────────────────────────────────────────────────────────────────
// Worker path
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {any} input
 * @returns {Array<{key: any, geo: any}>}
 */
function toJobList(input) {
  if (!input) return [];
  if (input instanceof Map) return [...input.entries()].map(([key, geo]) => ({ key, geo }));
  if (Array.isArray(input)) return input.map((geo, i) => ({ key: i, geo }));
  return [{ key: 0, geo: input }];
}

/**
 * Bakes one geometry, an array of them, or a `Map<MaterialKey, geometry>`
 * (i.e. `GeoBuilder.merge()` straight through) off the main thread, and APPLIES
 * the results.
 *
 * Falls back to the synchronous path — same numbers, same bytes — when there is
 * no `Worker` (node, tests), when `cfg.useWorker === false`, or when the worker
 * fails to start. The fallback yields to the event loop between geometries so
 * `onProgress` can actually paint.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * MESSAGE CONTRACT (also documented in `bake.worker.js`)
 * ─────────────────────────────────────────────────────────────────────────────
 * main -> worker  `{ type:'bake', id, cfg, occ, emitters,
 *                    jobs:[{ index, count, position, normal, albedo|null }] }`
 *   `position/normal/albedo` are COPIES (`.slice()`) and ARE transferred — the
 *   originals must never be, or the caller's geometry is neutered.
 *   `occ`/`emitters` are `toArrays()` output and are structured-CLONED.
 * worker -> main  `{ type:'progress', id, done, total, t }`
 *                 `{ type:'done', id, ms, results:[{index, color, color1}] }`  (transferred)
 *                 `{ type:'error', id, message, stack }`
 *
 * @param {any} geoOrGeos
 * @param {OccluderSet|null} occ
 * @param {EmitterSet|null} emitters
 * @param {Partial<BakeConfig>|null} [cfg]
 * @param {{onProgress?: (t: number, label?: string) => void}} [opts]
 * @returns {Promise<{ms: number, verts: number, worker: boolean, results: Array<{key: any, color: Float32Array, color1: Float32Array}>}>}
 */
export async function bakeAsync(geoOrGeos, occ, emitters, cfg = null, opts = {}) {
  const onProgress = opts.onProgress ?? null;
  const jobs = toJobList(geoOrGeos).filter((j) => j.geo?.getAttribute?.('position')?.count > 0);
  const merged = mergeConfig(DAY_DEFAULTS, cfg);
  const t0 = now();

  if (jobs.length === 0) return { ms: 0, verts: 0, worker: false, results: [] };

  let total = 0;
  for (const j of jobs) total += j.geo.getAttribute('position').count;

  const canWorker =
    merged.useWorker &&
    typeof Worker !== 'undefined' &&
    typeof URL !== 'undefined' &&
    typeof document !== 'undefined';

  if (canWorker) {
    try {
      const results = await runInWorker(jobs, occ, emitters, merged, total, onProgress);
      let verts = 0;
      for (let i = 0; i < jobs.length; i++) {
        if (merged.splitDay) applySplitBake(jobs[i].geo, results[i]);
        else applyBake(jobs[i].geo, results[i]);
        verts += jobs[i].geo.getAttribute('position').count;
      }
      onProgress?.(1, 'bake');
      return {
        ms: now() - t0,
        verts,
        worker: true,
        results: results.map((r, i) => ({ key: jobs[i].key, color: r.color, color1: r.color1 })),
      };
    } catch (err) {
      console.warn('[bake] worker path failed, falling back to the main thread', err);
    }
  }

  /** @type {Array<{key: any, color: Float32Array, color1: Float32Array}>} */
  const out = [];
  let done = 0;
  let verts = 0;
  for (const j of jobs) {
    // Yield so the loading overlay repaints between geometries.
    await new Promise((r) => setTimeout(r, 0));
    const res = bakeSync(j.geo, occ, emitters, merged);
    if (merged.splitDay) applySplitBake(j.geo, res);
    else applyBake(j.geo, res);
    done += res.verts;
    verts += res.verts;
    out.push({ key: j.key, color: res.color, color1: res.color1 });
    onProgress?.(total ? done / total : 1, 'bake');
  }
  onProgress?.(1, 'bake');
  return { ms: now() - t0, verts, worker: false, results: out };
}

/**
 * @param {Array<{key:any, geo:any}>} jobs
 * @param {OccluderSet|null} occ
 * @param {EmitterSet|null} emitters
 * @param {any} cfg
 * @param {number} total
 * @param {((t: number, label?: string) => void)|null} onProgress
 * @returns {Promise<Array<{color: Float32Array, color1: Float32Array}>>}
 */
function runInWorker(jobs, occ, emitters, cfg, total, onProgress) {
  return new Promise((resolve, reject) => {
    // Vite rewrites this exact form into a bundled module worker.
    const worker = new Worker(new URL('./bake.worker.js', import.meta.url), { type: 'module' });
    const id = ++runInWorker._id;
    /** @type {ArrayBufferLike[]} */
    const transfer = [];
    const payload = [];

    for (let i = 0; i < jobs.length; i++) {
      const geo = jobs[i].geo;
      const pos = geo.getAttribute('position');
      const nor = geo.getAttribute('normal');
      const col = geo.getAttribute('color');
      // COPY: transferring the live attribute arrays would detach the caller's
      // geometry (it would render as garbage, silently).
      const position = new Float32Array(pos.array);
      const normal = nor ? new Float32Array(nor.array) : null;
      const albedo = col ? new Float32Array(col.array) : null;
      transfer.push(position.buffer);
      if (normal) transfer.push(normal.buffer);
      if (albedo) transfer.push(albedo.buffer);
      payload.push({ index: i, count: pos.count, position, normal, albedo });
    }

    const cleanup = () => {
      worker.onmessage = null;
      worker.onerror = null;
      worker.terminate();
    };

    worker.onerror = (e) => {
      cleanup();
      reject(new Error(`bake.worker: ${e.message ?? 'failed to start'}`));
    };

    worker.onmessage = (ev) => {
      const m = ev.data;
      if (!m || m.id !== id) return;
      if (m.type === 'progress') {
        onProgress?.(total ? m.done / total : 0, 'bake');
      } else if (m.type === 'done') {
        cleanup();
        const out = new Array(jobs.length);
        // ⚠ CARRY `ambient`/`sunTint` THROUGH. They are the split day channel
        // (`cfg.splitDay`) and the worker has always sent them; rebuilding the
        // result with only `color`/`color1` made `applySplitBake` throw
        // "result was not baked with `splitDay: true`", which `bakeAsync`
        // catches — so the worker path SILENTLY fell back to the main thread on
        // every keyed level. Nothing looked wrong, because the fallback bakes
        // the same pixels; it just does it on the main thread, and the whole
        // loading screen stops repainting for as long as it takes. Undetectable
        // at 720 m (~1.5 s), 17 s at 2 km. `meta.stats.bakeWorker` is the tell.
        for (const r of m.results) {
          out[r.index] = { color: r.color, color1: r.color1, ambient: r.ambient, sunTint: r.sunTint };
        }
        resolve(out);
      } else if (m.type === 'error') {
        cleanup();
        reject(new Error(`bake.worker: ${m.message}\n${m.stack ?? ''}`));
      }
    };

    worker.postMessage(
      {
        type: 'bake',
        id,
        cfg,
        occ: occ ? occ.toArrays() : null,
        emitters: emitters ? emitters.toArrays() : null,
        jobs: payload,
      },
      transfer,
    );
  });
}
runInWorker._id = 0;
